"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { TableState } from "./use-table-state";

/**
 * One persisted view configuration. The shape is intentionally flat so the
 * payload survives JSON round-tripping unchanged.
 */
export interface SavedView {
  /** Stable, opaque identifier. Generated by {@link useSavedViews}. */
  id: string;
  /** User-facing label for the view. */
  name: string;
  /** Snapshot of the table state at the time the view was saved. */
  state: TableState;
}

/**
 * localStorage key prefix. Combined with `tableKey`, every table gets its
 * own bucket of saved views.
 */
export const SAVED_VIEWS_STORAGE_PREFIX = "aio:tableviews:";

/** Inputs to {@link useSavedViews}. */
export interface UseSavedViewsOptions {
  /** Stable identifier for this table — scopes the storage bucket. */
  tableKey: string;
  /** Current live table state. `save()` snapshots this value. */
  currentState: TableState;
  /**
   * Optional handler invoked when {@link UseSavedViewsResult.apply} is
   * called. The hook itself does not own the table state; the consumer
   * decides how to push the saved state into its own store.
   */
  onApply?: (state: TableState) => void;
}

/** Return shape of {@link useSavedViews}. */
export interface UseSavedViewsResult {
  /** All views for this table, in save order (oldest first). */
  views: SavedView[];
  /** The currently selected view, or null when none is applied. */
  current: SavedView | null;
  /** Save the current state under `name`. Returns the new view id. */
  save: (name: string) => string;
  /** Apply a stored view by id. Calls `onApply` with the view's state. */
  apply: (viewId: string) => void;
  /** Delete a stored view by id. */
  delete: (viewId: string) => void;
}

function storageKey(tableKey: string): string {
  return `${SAVED_VIEWS_STORAGE_PREFIX}${tableKey}`;
}

function readPersisted(tableKey: string): SavedView[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(storageKey(tableKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedView[]) : [];
  } catch (err) {
    console.warn(
      `[useSavedViews] Failed to hydrate views for "${tableKey}":`,
      err,
    );
    return [];
  }
}

function writePersisted(tableKey: string, views: SavedView[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(tableKey), JSON.stringify(views));
  } catch (err) {
    console.warn(
      `[useSavedViews] Failed to persist views for "${tableKey}":`,
      err,
    );
  }
}

/**
 * Generate a reasonably collision-resistant id. We avoid pulling in `uuid`
 * here because the entire point of saved views is that they live in
 * localStorage on the user's machine — a one-in-billions clash inside a
 * single user's bucket is fine.
 */
function newViewId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `view_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Manage a list of named table-state snapshots persisted to localStorage.
 *
 * The hook keeps a separate "current" pointer so the toolbar can show
 * which view is active. Saving a new view automatically becomes the
 * current one; applying a view sets it; deleting the current view clears
 * the pointer (the table falls back to whatever live state was already in
 * place).
 */
export function useSavedViews(options: UseSavedViewsOptions): UseSavedViewsResult {
  const { tableKey, currentState, onApply } = options;

  const [views, setViews] = useState<SavedView[]>(() => readPersisted(tableKey));
  const [currentId, setCurrentId] = useState<string | null>(null);

  // Keep refs in sync so callbacks below are stable but read fresh values.
  const stateRef = useRef(currentState);
  stateRef.current = currentState;
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  const tableKeyRef = useRef(tableKey);
  tableKeyRef.current = tableKey;

  // When `tableKey` changes (rare — but the hook is generic), re-hydrate.
  useEffect(() => {
    setViews(readPersisted(tableKey));
    setCurrentId(null);
  }, [tableKey]);

  const save = useCallback((name: string): string => {
    const id = newViewId();
    // Deep-clone via JSON so future mutations to the live state can't
    // leak into the saved snapshot.
    const snapshot: TableState = JSON.parse(JSON.stringify(stateRef.current));
    const view: SavedView = { id, name, state: snapshot };
    setViews((prev) => {
      const next = [...prev, view];
      writePersisted(tableKeyRef.current, next);
      return next;
    });
    setCurrentId(id);
    return id;
  }, []);

  const apply = useCallback((viewId: string): void => {
    setViews((prev) => {
      const view = prev.find((v) => v.id === viewId);
      if (!view) return prev;
      onApplyRef.current?.(view.state);
      setCurrentId(viewId);
      return prev;
    });
  }, []);

  const remove = useCallback((viewId: string): void => {
    setViews((prev) => {
      const next = prev.filter((v) => v.id !== viewId);
      writePersisted(tableKeyRef.current, next);
      return next;
    });
    setCurrentId((prev) => (prev === viewId ? null : prev));
  }, []);

  const current = currentId ? views.find((v) => v.id === currentId) ?? null : null;

  return {
    views,
    current,
    save,
    apply,
    delete: remove,
  };
}
